import React, { useEffect, useRef } from 'react';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAAA_EXAMPLE_KEY';

const CloudflareTurnstile = ({ onVerify, onError, onExpire, theme = 'dark', resetKey }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const widgetRenderedRef = useRef(false);

  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);
  const onExpireRef = useRef(onExpire);

  useEffect(() => {
    onVerifyRef.current = onVerify;
    onErrorRef.current = onError;
    onExpireRef.current = onExpire;
  }, [onVerify, onError, onExpire]);

  useEffect(() => {
    if (resetKey && widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch (e) {
      }
    }
  }, [resetKey]);

  useEffect(() => {
    if (widgetRenderedRef.current) return;

    const renderWidget = () => {
      if (!containerRef.current || !window.turnstile || widgetRenderedRef.current) return;

      widgetRenderedRef.current = true;

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: theme,
        callback: (token) => {
          if (onVerifyRef.current) onVerifyRef.current(token);
        },
        'error-callback': () => {
          if (onErrorRef.current) onErrorRef.current();
        },
        'expired-callback': () => {
          if (onExpireRef.current) onExpireRef.current();
        },
      });
    };

    if (window.turnstile) {
      renderWidget();
      return;
    }

    const existingScript = document.querySelector('script[src*="turnstile"]');
    if (existingScript) {
      if (window.turnstile) {
        renderWidget();
      } else {
        existingScript.addEventListener('load', renderWidget);
      }
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;

    script.onload = () => {
      renderWidget();
    };

    script.onerror = () => {
      if (onErrorRef.current) onErrorRef.current();
    };

    document.head.appendChild(script);

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
          widgetRenderedRef.current = false;
        } catch (e) {
        }
      }
    };
  }, [theme]);

  return (
    <div className="turnstile-container">
      <div ref={containerRef} className="turnstile-widget"></div>
    </div>
  );
};

export const verifyTurnstileToken = async (token) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/verify-turnstile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ token }),
    });

    const data = await response.json();
    return data.success;
  } catch (error) {
    return false;
  }
};

export default CloudflareTurnstile;
